import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { HealthBarComponent } from "./HealthBarComponent";
import { InventoryComponent } from "./InventoryComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 70%;
        height: 100%;
        color: black;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
        grid-row-gap: 0px;
        overflow: auto;
    }

    :host::-webkit-scrollbar {
        display: none;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        height: 25px;
        overflow: auto;
        color: white;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    .content p {
        margin: 0 0 10px 0;
        color: white;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer-container {
        display: flex;
        flex-direction: row;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #d77bba;
        border: 1px solid #332c57;
        margin-top: 10px;
        display: flex;
        height: 105px;
        width: 100%;
        overflow: auto;
    }

    .footer-character {
        border-radius: 10px 10px 0 0;
        background-color: #d77bba;
        border: 1px solid #332c57;
        margin-top: 10px;
        display: flex;
        height: 105px;
        width: 10%;
    }

    .footer-character img {
        width: 100px;
        height: 100px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: white;
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #9e397e;
    }

    .footer::-webkit-scrollbar {
        height: 8px;
    }

    .footer::-webkit-scrollbar-track {
        background: #d77bba;
        border-radius: 10px;
    }

    .footer::-webkit-scrollbar-thumb {
        background: #9e397e;
        border-radius: 10px;
        border: 2px solid #d77bba;
    }

    .footer::-webkit-scrollbar-thumb:hover {
        background: #7c2e64;
    }

    .inventory-container {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        width: 100%;
        padding: 5px;
    }

    .health-bars {
        display: flex;
        justify-content: space-between;
        padding: 5px;
        margin-top: -200px;
        max-width: 100%;
        padding: 5px;
    }

    .health-bar-box {
        width: 500px;
        height: 30px;
    }

    .health-bar-fill {
        height: 60%;
        background-color: red;
        border-radius: 5px;
        border: 1px solid white;
    }

    .health-bar-border {
        background-color: #ffffff;
        border: 1px solid black;
        border-radius: 5px;
        margin: 0;
    }

    .player-text {
        color: white;
    }

    .boss-text {
        color: white;
    }
    
    .inventory-boxes {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        background-color: #d77bba;
        border: 1px solid black;
        padding: 5px;
        border-radius: 5px;
        margin: 0;
        margin-right: 5px;
    }

    .inventory-boxes:hover {
        cursor: pointer;
        background-color: #9e397e;
    }

    .img-inventory {
        width: 50px;
        height: 50px;
    }

    .sound-button {
        display: flex;
        justify-content: center;
        padding: 5px;
        background-color: #d77bba;
        border: 1px solid #332c57;
        border-radius: 5px;
    }

    .inventory-and-sound {
        display: flex;
        justify-content: space-between;
        padding: 5px;
    }

    #sound {
        width: 50px;
        height: 50px;
    }

    .sound-button:hover {
        cursor: pointer;
        background-color: #9e397e;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
            ${styles}
            </style>
            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${
            this._currentGameState?.roomAlias === "BossFightRoom"
                ? `
                <div class="health-bars">
                    <div class="health-bar-name-and-bar">
                    <p class="player-text">Healthbar Player:</p>
                    <div class="health-bar-box">
                        <div class="health-bar-fill" style="width: ${this.returnPlayerHealthBar()}%;"></div>
                    </div>
                    </div>
                    <div class="health-bar-name-and-bar">
                    <p class="boss-text">Healthbar Boss:</p>
                    <div class="health-bar-box">
                        <div class="health-bar-fill" style="width: ${this.returnBossHealthBar()}%;"></div>
                    </div>
                    </div>
                </div>
                `
                : ""
            }
            <div class="inventory-and-sound">
                <game-inventory></game-inventory>
                <game-sound></game-sound>
            </div>
            <div class="footer-container">
            ${this.renderFooter()}
            </div>
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        this.shadowRoot.querySelectorAll("game-inventory").forEach(element => {
            (element as InventoryComponent).data = this.getInventoryData();
        }
        );

        this.shadowRoot.querySelectorAll("game-health-player").forEach(element => {
            (element as HealthBarComponent).setHealthPlayer(this.returnPlayerHealthBar());
        });
        this.shadowRoot.querySelectorAll("game-health-boss").forEach(element => {
            (element as HealthBarComponent).setHealthBoss(this.returnBossHealthBar());
        });
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
    }

    private getInventoryData(): string[] {
        const inventoryItems: string[] | undefined = Array.isArray(this._currentGameState?.inventory)
            ? this._currentGameState.inventory
            : undefined;
        return inventoryItems ?? [];
    }

    private returnPlayerHealthBar(): number {
        const healthPlayer: number | undefined = this._currentGameState?.healthPlayer;
        if (healthPlayer === undefined) {
            return 0;
        }

        return healthPlayer;
    }

    private returnBossHealthBar(): number {
        const healthBoss: number | undefined = this._currentGameState?.healthBoss;
        if (healthBoss === undefined) {
            return 0;
        }

        return healthBoss;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
